import { useState } from "react";
import { createPortal } from "react-dom";
import { useRangeScope, PRESETS, presetPeriod, daysPeriod, stepPeriod, canStepForward, periodLabel, periodDays, addDays, dayStart, type Period } from "../../lib/rangeScope";

/**
 * Reports' period (canvas v2-o-analytics-range-proposed, phone v2-om-analytics-range-proposed and -sheet-proposed):
 * ‹ and › step the same span back and forward; the key opens quick picks and a calendar, and nothing changes until "Show".
 */
export function PeriodPicker({ phone }: { phone: boolean }) {
  const { period, setPeriod, compare, setCompare } = useRangeScope();
  const [open, setOpen] = useState(false);
  const now = new Date();
  const label = periodLabel(period, now);
  const forward = canStepForward(period, now);
  const span = periodDays(period) === 1 ? "day" : `${periodDays(period)} days`;

  return (
    <div className="o-period">
      <button type="button" className="o-period__step" aria-label={`Previous ${span}`} onClick={() => setPeriod(stepPeriod(period, -1, now))}>
        <span className="ms" aria-hidden="true">chevron_left</span>
      </button>
      <button type="button" className={`o-period__key${open ? " on" : ""}`} aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((v) => !v)}>
        <span className="ms o-period__ico" aria-hidden="true">calendar_month</span>
        <span className="o-period__text">
          <b>{label.title}</b>
          <span className="num">{label.dates}</span>
        </span>
        <span className="ms o-period__chev" aria-hidden="true">{open && !phone ? "expand_less" : "expand_more"}</span>
      </button>
      <button type="button" className="o-period__step" aria-label={`Next ${span}`} disabled={!forward} onClick={() => setPeriod(stepPeriod(period, 1, now))}>
        <span className="ms" aria-hidden="true">chevron_right</span>
      </button>
      {open && (
        <PeriodPanel
          phone={phone}
          period={period}
          compare={compare}
          onClose={() => setOpen(false)}
          onShow={(p, c) => {
            setPeriod(p);
            setCompare(c);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function PeriodPanel({ phone, period, compare, onClose, onShow }: { phone: boolean; period: Period; compare: boolean; onClose: () => void; onShow: (p: Period, compare: boolean) => void }) {
  const now = new Date();
  const today = dayStart(now);
  const [draft, setDraft] = useState<Period>(period);
  // First tap picks the start and waits for the end; a second tap closes the range.
  const [anchor, setAnchor] = useState<Date | null>(null);
  const [cmp, setCmp] = useState(compare);
  const last = addDays(draft.end, -1);
  // The calendar opens on the period's last month (desktop shows the month before it too).
  const [month, setMonth] = useState(() => new Date(last.getFullYear(), last.getMonth(), 1));
  const months = phone ? [month] : [new Date(month.getFullYear(), month.getMonth() - 1, 1), month];
  const atToday = month.getFullYear() === today.getFullYear() && month.getMonth() === today.getMonth();
  const days = periodDays(draft);

  function pickDay(d: Date) {
    if (!anchor) {
      setAnchor(d);
      setDraft(daysPeriod(d, d));
    } else {
      setDraft(daysPeriod(anchor, d));
      setAnchor(null);
    }
  }
  function pickPreset(key: (typeof PRESETS)[number]["key"]) {
    const p = presetPeriod(key, now);
    setDraft(p);
    setAnchor(null);
    const l = addDays(p.end, -1);
    setMonth(new Date(l.getFullYear(), l.getMonth(), 1));
  }
  const fmt = (d: Date) => d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", ...(phone ? {} : { year: "numeric" }) });
  const span = days === 1 ? "1 day" : `${days} days`;

  const presets = (
    <div className="o-period__presets" role="radiogroup" aria-label="Quick picks">
      {!phone && <div className="o-period__gl">Quick picks</div>}
      {PRESETS.filter((p) => !phone || p.key !== "this_week").map((p) => (
        <button type="button" role="radio" key={p.key} aria-checked={draft.preset === p.key} onClick={() => pickPreset(p.key)}>
          {p.label}
          {!phone && draft.preset === p.key && <span className="ms" aria-hidden="true">check</span>}
        </button>
      ))}
    </div>
  );
  const calendar = (
    <div className="o-period__cal">
      <button type="button" className="o-period__nav" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>
        <span className="ms" aria-hidden="true">chevron_left</span>
      </button>
      {months.map((m) => (
        <Month key={m.toISOString()} month={m} draft={draft} today={today} onPick={pickDay} />
      ))}
      <button type="button" className="o-period__nav" aria-label="Next month" disabled={atToday} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>
        <span className="ms" aria-hidden="true">chevron_right</span>
      </button>
    </div>
  );
  const body = (
    <>
      {calendar}
      <div className="o-period__fields">
        <div>
          <span>From</span>
          <b className="num">{fmt(draft.start)}</b>
        </div>
        <div>
          <span>To</span>
          <b className="num">{anchor ? "Pick the last day" : fmt(last)}</b>
        </div>
      </div>
    </>
  );
  const compareSwitch = (
    <label className="o-period__cmp">
      <input type="checkbox" className="o-period__switch" checked={cmp} onChange={(e) => setCmp(e.target.checked)} />
      Compare with the {days === 1 ? "day" : `${days} days`} before
    </label>
  );
  const show = (
    <button type="button" className="o-btn o-period__show" disabled={!!anchor} onClick={() => onShow(draft, cmp)}>
      Show {span}
    </button>
  );

  if (phone) {
    // On body, so the sheet rises over the bottom bar and the bell instead of inside the page's sheet.
    return createPortal(
      <div className="o-period__shade" onClick={onClose}>
        <div role="dialog" aria-label="Pick a period" className="o-period__sheet" onClick={(e) => e.stopPropagation()}>
          <i className="o-period__grip" aria-hidden="true" />
          <div className="o-period__sheethead">
            <b>Pick a period</b>
            <button type="button" className="o-icon-btn" aria-label="Close" onClick={onClose}>
              <span className="ms">close</span>
            </button>
          </div>
          {presets}
          <div className="o-period__sheetbody">
            {body}
            {compareSwitch}
            {show}
          </div>
        </div>
      </div>,
      document.body,
    );
  }
  return (
    <>
      <div className="o-period__catch" onClick={onClose} />
      <div role="dialog" aria-label="Pick a period" className="o-period__panel">
        {presets}
        <div className="o-period__main">
          {body}
          <div className="o-period__foot">
            {compareSwitch}
            <span style={{ flex: 1 }} />
            <button type="button" className="o-btn o-btn--grey" onClick={onClose}>
              Cancel
            </button>
            {show}
          </div>
        </div>
      </div>
    </>
  );
}

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

function Month({ month, draft, today, onPick }: { month: Date; draft: Period; today: Date; onPick: (d: Date) => void }) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7; // weeks start on Monday
  const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const last = addDays(draft.end, -1);
  return (
    <div className="o-period__month">
      <div className="o-period__mname">{first.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</div>
      <div className="o-period__grid">
        {WEEKDAYS.map((w) => (
          <span key={w} className="o-period__wd">
            {w}
          </span>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <span key={`l${i}`} />
        ))}
        {Array.from({ length: count }, (_, i) => {
          const d = new Date(month.getFullYear(), month.getMonth(), i + 1);
          const t = d.getTime();
          const isStart = t === draft.start.getTime();
          const isEnd = t === last.getTime();
          const inRange = d >= draft.start && d < draft.end;
          const cls = ["o-period__day", inRange && "in", isStart && "start", isEnd && "end", t === today.getTime() && "today"].filter(Boolean).join(" ");
          return (
            <span key={i} className={cls}>
              <button type="button" className="num" disabled={d > today} aria-pressed={isStart || isEnd} onClick={() => onPick(d)}>
                {i + 1}
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
